const isDev = process.env.NODE_ENV === "development";

const CACHE_PREFIX = "konn3ct:commercial-pricing:v3:";
const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // stale data can render for up to 24h
const memoryCache = new Map();
const inFlight = new Map();

export const normalizeDevApiBase = (url) => {
  const clean = String(url || "").replace(/\/+$/, "");
  if (
    process.env.NODE_ENV !== "production" &&
    /^https?:\/\/(127\.0\.0\.1|localhost):8080$/i.test(clean)
  ) {
    return "http://127.0.0.1:8081";
  }
  return clean;
};

export const getApiBase = () => {
  const envUrl =
    process.env.REACT_APP_KAY_API_BASE_URL ||
    process.env.REACT_APP_PUBLIC_API_BASE_URL;

  if (envUrl) {
    return normalizeDevApiBase(envUrl);
  }

  return isDev ? "" : "https://backend-api.konn3ct.ng";
};

const buildApiUrl = (endpoint) => {
  const base = getApiBase();
  const cleanEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  const apiPath = cleanEndpoint.startsWith("/api/") ? cleanEndpoint : `/api${cleanEndpoint}`;
  return base ? `${base}${apiPath}` : apiPath;
};

const normalizeCurrency = (currency) =>
  String(currency || "NGN").trim().toUpperCase();

const cacheKey = (currency) => `${CACHE_PREFIX}${normalizeCurrency(currency)}`;

const readLocalCache = (currency) => {
  if (typeof window === "undefined" || !window.localStorage) return null;

  try {
    const raw = window.localStorage.getItem(cacheKey(currency));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed?.data || !parsed?.savedAt) return null;

    // Do not keep very old pricing indefinitely.
    if (Date.now() - Number(parsed.savedAt) > CACHE_MAX_AGE_MS) {
      window.localStorage.removeItem(cacheKey(currency));
      return null;
    }

    return parsed.data;
  } catch {
    return null;
  }
};

const writeLocalCache = (currency, data) => {
  if (typeof window === "undefined" || !window.localStorage || !data) return;

  try {
    window.localStorage.setItem(
      cacheKey(currency),
      JSON.stringify({
        savedAt: Date.now(),
        data,
      }),
    );
  } catch {
    // Storage may be unavailable/private/full. Memory cache still works.
  }
};

export const getCachedCommercialPlans = (currency = "NGN") => {
  const key = normalizeCurrency(currency);

  if (memoryCache.has(key)) {
    return memoryCache.get(key);
  }

  const local = readLocalCache(key);
  if (local) {
    memoryCache.set(key, local);
    return local;
  }

  return null;
};

export const fetchCommercialPlans = async (
  currency = "NGN",
  { signal, forceRefresh = false } = {},
) => {
  const normalized = normalizeCurrency(currency);

  if (!forceRefresh && !isDev) {
    const cached = getCachedCommercialPlans(normalized);
    if (cached) return cached;
  }

  // Avoid duplicate simultaneous requests for the same currency.
  if (inFlight.has(normalized) && !signal && !forceRefresh) {
    return inFlight.get(normalized);
  }

  const endpoint = buildApiUrl(
    `/v1/commercial/plans?currency=${encodeURIComponent(normalized)}`,
  );

  const request = (async () => {
    const response = await fetch(endpoint, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      cache: "no-store",
      signal,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch pricing plans: HTTP ${response.status}`);
    }

    const body = await response.json();

    memoryCache.set(normalized, body);
    writeLocalCache(normalized, body);

    return body;
  })();

  if (!signal) {
    inFlight.set(normalized, request);
  }

  try {
    return await request;
  } catch (error) {
    const cachedFallback = getCachedCommercialPlans(normalized);
    if (cachedFallback) {
      return cachedFallback;
    }
    throw error;
  } finally {
    if (!signal) {
      inFlight.delete(normalized);
    }
  }
};

export const clearCommercialPlansCache = (currency) => {
  if (currency) {
    const normalized = normalizeCurrency(currency);
    memoryCache.delete(normalized);

    if (typeof window !== "undefined" && window.localStorage) {
      try {
        window.localStorage.removeItem(cacheKey(normalized));
      } catch {}
    }
    return;
  }

  memoryCache.clear();

  if (typeof window !== "undefined" && window.localStorage) {
    try {
      Object.keys(window.localStorage)
        .filter((key) => key.startsWith(CACHE_PREFIX))
        .forEach((key) => window.localStorage.removeItem(key));
    } catch {}
  }
};
